import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { useAuth } from '../context/AuthContext';
import { useSavedProperties } from '../hooks/useSavedProperties';
import { toast } from 'sonner';
import { PlusCircle, Home, Eye, Clock, CheckCircle, XCircle, Trash2, Edit, Phone, Save, Loader2, Heart, BarChart2, CheckSquare, Square, Layers, AlertTriangle, Shield, X, MessageCircle, Share2 } from 'lucide-react';
import ShareListingModal from '../components/ShareListingModal';

const API = '/api';

const STATUS_CONFIG = {
  live: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Live' },
  pending: { icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Under Review' },
  rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Rejected' },
};

const STATUS_BADGE_CLS = {
  live:     'bg-green-100 text-green-700',
  pending:  'bg-amber-100 text-amber-700',
  paused:   'bg-slate-100 text-slate-600',
  rejected: 'bg-red-100 text-red-600',
};

// ── Extracted sub-component ──────────────────────────────────────────────────
function DeleteAccountModal({ initialPhone = '', onClose, onDeleted }) {
  const [step, setStep] = useState('confirm');
  const [phone, setPhone] = useState(initialPhone.replace(/\D/g, '').slice(-10));
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [masked, setMasked] = useState('');

  const handleSendOtp = async () => {
    if (phone.length !== 10) return toast.error('Enter a valid 10-digit mobile number');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/mobile-otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ mobile: phone })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setMasked(data.message);
      setStep('otp');
      toast.success('OTP sent to your mobile!');
    } catch (err) { toast.error(err.message || 'Failed to send OTP'); }
    setLoading(false);
  };

  const handleConfirmDelete = async () => {
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/account`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ mobile: phone, otp })
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Failed to delete account');
      }
      toast.success('Account deleted. Goodbye!');
      onDeleted();
    } catch (err) { toast.error(err.message || 'Failed to delete account'); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="delete-account-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 p-1 rounded-lg" data-testid="close-delete-modal">
          <X className="w-5 h-5" />
        </button>
        {step === 'confirm' ? (
          <div>
            <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-5">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Account?</h3>
            <p className="text-sm text-slate-500 mb-5 leading-relaxed">
              This will permanently delete your account, all your listings, saved properties, and notifications. <strong className="text-red-600">This action cannot be undone.</strong>
            </p>
            <div className="mb-5">
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Confirm your mobile number to proceed</label>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 flex-shrink-0">
                  <span>🇮🇳</span> +91
                </span>
                <input
                  type="tel" value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
                  placeholder="9876543210"
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 font-semibold tracking-widest"
                  data-testid="delete-phone-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="cancel-delete-btn">Cancel</button>
              <button onClick={handleSendOtp} disabled={loading || phone.length !== 10}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="send-delete-otp-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                Send OTP
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mb-5">
              <Shield className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Verify to Delete</h3>
            <p className="text-sm text-slate-500 mb-5">{masked || `OTP sent to +91-${phone}`}</p>
            <div className="mb-5">
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Enter 6-digit OTP</label>
              <input
                type="text" value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="• • • • • •"
                className="w-full px-4 py-4 border border-red-200 rounded-xl text-2xl font-bold tracking-[0.5em] text-center outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100"
                maxLength={6} data-testid="delete-otp-input" autoFocus
              />
            </div>
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5 text-xs text-red-700">
              After confirmation, your account and all data will be permanently deleted.
            </div>
            <div className="flex gap-3">
              <button onClick={() => setStep('confirm')} className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors" data-testid="back-delete-btn">Back</button>
              <button onClick={handleConfirmDelete} disabled={loading || otp.length !== 6}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                data-testid="confirm-delete-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {loading ? 'Deleting...' : 'Yes, Delete Account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// ────────────────────────────────────────────────────────────────────────────

// ── Chart constants ─────────────────────────────────────────────
const CHART_MARGIN = { top: 0, right: 0, left: -20, bottom: 0 };
const CHART_TICK = { fontSize: 11 };
const BAR_RADIUS_TOP = [4, 4, 0, 0];

export default function DashboardPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const { savedIds, toggleSave, isSaved } = useSavedProperties();
  const [tab, setTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [shareProperty, setShareProperty] = useState(null);
  const [savedProps, setSavedProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedLoading, setSavedLoading] = useState(false);
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [propStats, setPropStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/properties/my`, { credentials: 'same-origin' });
      if (res.ok) setListings(await res.json());
    } catch (_err) { console.error('fetchListings failed:', _err); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const res = await fetch(`${API}/properties/saved`, { credentials: 'same-origin' });
      if (res.ok) setSavedProps(await res.json());
    } catch (_err) { console.error('fetchSaved failed:', _err); }
    setSavedLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllStats = useCallback(async () => {
    if (!listings.length) return;
    setStatsLoading(true);
    const livePaid = listings.filter(p => p.status === 'live' && p.listing_type !== 'free');
    const toFetch = livePaid.length > 0 ? livePaid : listings.slice(0, 3);
    const results = await Promise.all(
      toFetch.map(p =>
        fetch(`${API}/properties/${p.property_id}/stats`, { credentials: 'same-origin' })
          .then(r => r.ok ? r.json() : null)
      )
    );
    const map = {};
    results.filter(Boolean).forEach(s => { map[s.property_id] = s; });
    setPropStats(map);
    setStatsLoading(false);
  }, [listings]);

  useEffect(() => {
    if (!user) return;
    setPhone(user.phone || '');
    fetchListings();
  }, [user, fetchListings]);

  useEffect(() => {
    if (tab === 'saved') fetchSaved();
    if (tab === 'analytics') fetchAllStats();
  }, [tab, fetchSaved, fetchAllStats]);

  // Fetch chat unread count
  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API}/chat/unread-count`, { credentials: 'same-origin' });
        if (res.ok) { const d = await res.json(); setChatUnread(d.unread || 0); }
      } catch (_err) { console.error('fetchUnread failed:', _err); }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 10000);
    return () => clearInterval(iv);
  }, [user]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(listings.map(p => p.property_id)));
  const deselectAll = () => setSelectedIds(new Set());

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) return;
    const label = action === 'delete' ? `Delete ${selectedIds.size} listing(s)?` : `${action === 'activate' ? 'Activate' : 'Pause'} ${selectedIds.size} listing(s)?`;
    if (!window.confirm(label)) return;
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/properties/bulk-action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ action, property_ids: Array.from(selectedIds) })
      });
      const data = await res.json();
      toast.success(`Done: ${data.affected} listing(s) affected`);
      setSelectedIds(new Set());
      setBulkMode(false);
      fetchListings();
    } catch { toast.error('Bulk action failed'); }
    setBulkLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try {
      const res = await fetch(`${API}/properties/${id}`, { method: 'DELETE', credentials: 'same-origin' });
      if (!res.ok) throw new Error('Failed to delete');
      setListings(prev => prev.filter(p => p.property_id !== id));
      toast.success('Property deleted');
    } catch {
      toast.error('Could not delete property');
    }
  };

  const [selectedRole, setSelectedRole] = useState(user?.role || 'tenant');

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // Save profile (phone)
      const res = await fetch(`${API}/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ phone })
      });
      if (!res.ok) throw new Error();
      // Save role if changed
      if (selectedRole !== user?.role) {
        const roleRes = await fetch(`${API}/auth/role`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ role: selectedRole })
        });
        if (!roleRes.ok) throw new Error('Failed to update role');
      }
      const updated = await res.json();
      updateUser({ ...updated, role: selectedRole });
      toast.success('Profile updated!');
    } catch (_err) {
      toast.error('Failed to save profile');
    }
    setSavingProfile(false);
  };

  const handleToggleSave = async (propertyId) => {
    try {
      const saved = await toggleSave(propertyId);
      toast.success(saved ? 'Saved to favorites!' : 'Removed from saved');
      if (!saved) setSavedProps(prev => prev.filter(p => p.property_id !== propertyId));
    } catch (_err) {
      toast.error('Failed to update saved');
    }
  };

  const openDeleteModal = () => setShowDeleteModal(true);

  const stats = {
    total: listings.length,
    live: listings.filter(p => p.status === 'live').length,
    pending: listings.filter(p => p.status === 'pending').length,
    totalViews: listings.reduce((s, p) => s + (p.views || 0), 0),
  };

  const canList = user && ['owner', 'broker', 'admin'].includes(user.role);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>My Dashboard | Rent Mitra</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-500 text-sm mt-0.5 capitalize">{user?.role} Dashboard</p>
          </div>
          {canList && (
            <Link to="/add-property" className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors shadow-sm" data-testid="add-property-button">
              <PlusCircle className="w-4 h-4" />Add Property
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        {canList && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
            {[
              { label: 'Total Listings', value: stats.total, icon: Home, color: 'bg-blue-50 text-blue-700' },
              { label: 'Live Listings', value: stats.live, icon: CheckCircle, color: 'bg-green-50 text-green-700' },
              { label: 'Under Review', value: stats.pending, icon: Clock, color: 'bg-amber-50 text-amber-700' },
              { label: 'Total Views', value: stats.totalViews, icon: Eye, color: 'bg-purple-50 text-purple-700' },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${s.color}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl border border-slate-200 p-1.5 mb-6 w-fit flex-wrap">
          {[
            { id: 'listings', label: 'My Listings' },
            { id: 'saved', label: 'Saved', icon: Heart },
            ...(canList ? [{ id: 'analytics', label: 'Analytics', icon: BarChart2 }] : []),
            { id: 'messages', label: 'Messages', icon: MessageCircle },
            { id: 'profile', label: 'Profile' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-2 rounded-xl text-sm font-semibold transition-all flex items-center gap-1.5 ${tab === t.id ? 'bg-blue-700 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid={`tab-${t.id}`}>
              {t.icon && <t.icon className="w-3.5 h-3.5" />}
              {t.label}
              {t.id === 'saved' && savedIds.size > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === 'saved' ? 'bg-blue-600 text-white' : 'bg-red-100 text-red-600'}`}>{savedIds.size}</span>
              )}
              {t.id === 'messages' && chatUnread > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold min-w-[20px] text-center ${tab === 'messages' ? 'bg-blue-600 text-white' : 'bg-red-500 text-white'}`}>{chatUnread > 9 ? '9+' : chatUnread}</span>
              )}
            </button>
          ))}
        </div>

        {/* Listings Tab */}
        {tab === 'listings' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              </div>
            ) : !canList ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <div className="text-4xl mb-4">🏠</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Looking for a home?</h3>
                <p className="text-slate-500 text-sm mb-5">Start searching for rental properties across India.</p>
                <Link to="/search" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors" data-testid="search-properties-btn">
                  Search Properties
                </Link>
              </div>
            ) : listings.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <div className="text-4xl mb-4">📋</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">No listings yet</h3>
                <p className="text-slate-500 text-sm mb-5">Add your first property listing to get started.</p>
                <Link to="/add-property" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors" data-testid="add-first-property">
                  Add Property
                </Link>
              </div>
            ) : (
              <>
                {/* Listing actions header */}
                <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                  <p className="text-sm text-slate-500">{listings.length} listing{listings.length !== 1 ? 's' : ''}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setBulkMode(m => !m); setSelectedIds(new Set()); }}
                      className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-xl border transition-all ${bulkMode ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}
                      data-testid="bulk-mode-toggle"
                    >
                      <Layers className="w-4 h-4" />
                      {bulkMode ? 'Exit Bulk Mode' : 'Bulk Manage'}
                    </button>
                    <Link to="/add-property"
                      className="flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
                      data-testid="add-property-btn">
                      <PlusCircle className="w-4 h-4" /> Add Property
                    </Link>
                  </div>
                </div>

                {/* Bulk action bar */}
                {bulkMode && (
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl px-5 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-action-bar">
                    <div className="flex items-center gap-3">
                      <button onClick={selectedIds.size === listings.length ? deselectAll : selectAll}
                        className="flex items-center gap-1.5 text-sm text-blue-700 font-semibold" data-testid="select-all-btn">
                        {selectedIds.size === listings.length ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        {selectedIds.size === listings.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-0.5 rounded-full">{selectedIds.size} selected</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleBulkAction('activate')} disabled={selectedIds.size === 0 || bulkLoading}
                        className="text-xs font-bold px-3 py-2 bg-green-100 text-green-700 hover:bg-green-200 rounded-xl disabled:opacity-40 transition-colors" data-testid="bulk-activate-btn">
                        Activate ({selectedIds.size})
                      </button>
                      <button onClick={() => handleBulkAction('deactivate')} disabled={selectedIds.size === 0 || bulkLoading}
                        className="text-xs font-bold px-3 py-2 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-xl disabled:opacity-40 transition-colors" data-testid="bulk-pause-btn">
                        Pause ({selectedIds.size})
                      </button>
                      <button onClick={() => handleBulkAction('delete')} disabled={selectedIds.size === 0 || bulkLoading}
                        className="text-xs font-bold px-3 py-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-xl disabled:opacity-40 transition-colors" data-testid="bulk-delete-btn">
                        {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> : null}Delete ({selectedIds.size})
                      </button>
                    </div>
                  </div>
                )}

                {/* Listings: bulk list or normal list */}
                {bulkMode ? (
                  <div className="space-y-2" data-testid="bulk-listings-list">
                    {listings.map(prop => {
                      const sel = selectedIds.has(prop.property_id);
                      return (
                        <div
                          key={prop.property_id}
                          onClick={() => toggleSelect(prop.property_id)}
                          className={`flex items-center gap-4 bg-white border rounded-2xl px-4 py-3 cursor-pointer transition-all ${sel ? 'border-blue-400 bg-blue-50/40 shadow-sm' : 'border-slate-200 hover:border-blue-200'}`}
                          data-testid={`bulk-item-${prop.property_id}`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all ${sel ? 'bg-blue-700 border-blue-700' : 'border-slate-300'}`}>
                            {sel && <CheckSquare className="w-3.5 h-3.5 text-white" />}
                          </div>
                          {prop.images?.[0] && <img src={prop.images[0]} alt="" className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">{prop.title || `${prop.bhk_type || ''} ${prop.property_type}`}</p>
                            <p className="text-xs text-slate-500">{prop.city} · ₹{Number(prop.rent).toLocaleString('en-IN')}/mo</p>
                          </div>
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize flex-shrink-0 ${STATUS_BADGE_CLS[prop.status] || STATUS_BADGE_CLS.rejected}`}>
                            {prop.status}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="my-listings">
                    {listings.map(prop => {
                      const sc = STATUS_CONFIG[prop.status] || STATUS_CONFIG.pending;
                      return (
                        <div key={prop.property_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex gap-4 items-start" data-testid={`listing-${prop.property_id}`}>
                          <div className="w-24 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                            {prop.images?.[0] ? (
                              <img src={prop.images[0]} alt={prop.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full img-placeholder flex items-center justify-center text-slate-300 text-xl">🏠</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div>
                                <h4 className="font-semibold text-slate-900 truncate text-sm">{prop.title || `${prop.bhk_type || ''} ${prop.property_type}`}</h4>
                                <p className="text-xs text-slate-500 mt-0.5">{prop.area}{prop.city ? `, ${prop.city}` : ''}</p>
                              </div>
                              <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.color}`}>
                                <sc.icon className="w-3 h-3" />{sc.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-sm font-bold text-blue-700">₹{Number(prop.rent).toLocaleString('en-IN')}/mo</span>
                              <span className="flex items-center gap-1 text-xs text-slate-400"><Eye className="w-3 h-3" />{prop.views || 0}</span>
                              <span className="text-xs text-slate-400 capitalize">{prop.listing_type}</span>
                            </div>
                            {prop.status === 'rejected' && prop.rejection_reason && (
                              <p className="text-xs text-red-600 mt-1.5 bg-red-50 px-2 py-1 rounded-lg">Reason: {prop.rejection_reason}</p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={() => setShareProperty(prop)}
                              className="p-2 hover:bg-green-50 rounded-lg transition-colors text-green-600" title="Share" data-testid={`share-${prop.property_id}`}>
                              <Share2 className="w-4 h-4" />
                            </button>
                            <button onClick={() => navigate(`/edit-property/${prop.property_id}`)}
                              className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600" title="Edit" data-testid={`edit-${prop.property_id}`}>
                              <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(prop.property_id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-500" title="Delete" data-testid={`delete-${prop.property_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Saved Tab */}
        {tab === 'saved' && (
          <div>
            {savedLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              </div>
            ) : savedProps.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="no-saved">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-8 h-8 text-red-300" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">No saved properties</h3>
                <p className="text-slate-500 text-sm mb-5">Browse properties and tap the heart icon to save your favorites.</p>
                <Link to="/search" className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors" data-testid="browse-properties-btn">
                  Browse Properties
                </Link>
              </div>
            ) : (
              <div>
                <p className="text-slate-500 text-sm mb-4">{savedProps.length} saved {savedProps.length === 1 ? 'property' : 'properties'}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="saved-properties-grid">
                  {savedProps.map(p => (
                    <PropertyCard
                      key={p.property_id}
                      property={p}
                      isSaved={isSaved(p.property_id)}
                      onToggleSave={handleToggleSave}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {tab === 'analytics' && (
          <div data-testid="analytics-tab">
            {statsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              </div>
            ) : Object.keys(propStats).length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <BarChart2 className="w-10 h-10 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-slate-900 mb-2">No analytics yet</h3>
                <p className="text-slate-500 text-sm">Add live listings to start tracking performance.</p>
                <button onClick={fetchAllStats} className="mt-4 text-blue-600 text-sm font-medium hover:underline" data-testid="load-analytics-btn">Load Analytics</button>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.values(propStats).map(s => (
                  <div key={s.property_id} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid={`analytics-card-${s.property_id}`}>
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div>
                        <h3 className="font-bold text-slate-900 text-base line-clamp-1" style={{fontFamily:'Outfit,sans-serif'}}>{s.title}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block capitalize ${s.listing_type === 'free' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                          {s.listing_type === 'free' ? 'Free Plan' : `${s.listing_type} Plan`}
                        </span>
                      </div>
                      <div className="flex gap-4 text-center flex-shrink-0">
                        {[
                          { label: 'Views', val: s.total_views },
                          { label: 'Contacts', val: s.total_contacts },
                          { label: 'Saves', val: s.total_saves },
                        ].map(m => (
                          <div key={m.label}>
                            <div className="text-xl font-bold text-slate-900">{m.val}</div>
                            <div className="text-xs text-slate-500">{m.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {s.listing_type !== 'free' ? (
                      <>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Monthly Contacts & Saves</p>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={s.monthly} margin={CHART_MARGIN}>
                            <XAxis dataKey="month" tick={CHART_TICK} />
                            <YAxis tick={CHART_TICK} allowDecimals={false} />
                            <Tooltip />
                            <Bar dataKey="contacts" fill="#1D4ED8" radius={BAR_RADIUS_TOP} name="Contacts" />
                            <Bar dataKey="saves" fill="#FCA5A5" radius={BAR_RADIUS_TOP} name="Saves" />
                          </BarChart>
                        </ResponsiveContainer>
                      </>
                    ) : (
                      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-amber-700">
                        Upgrade to a paid plan to unlock monthly trend charts and detailed analytics.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Messages Tab */}
        {tab === 'messages' && (
          <div data-testid="messages-tab-panel">
            {!user?.is_phone_verified ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Mobile Verification Required</h3>
                <p className="text-slate-500 text-sm mb-5">Verify your mobile number to access secure chat.</p>
                <Link to="/verify-mobile" className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors inline-block">Verify Mobile</Link>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Your Messages</h3>
                <p className="text-slate-500 text-sm mb-5">
                  {chatUnread > 0
                    ? `You have ${chatUnread} unread conversation${chatUnread > 1 ? 's' : ''}.`
                    : 'Chat privately with owners and tenants — no contact details shared.'}
                </p>
                <Link
                  to="/messages"
                  className="bg-blue-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-800 transition-colors inline-flex items-center gap-2"
                  data-testid="open-messages-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  {chatUnread > 0 ? `Open Inbox (${chatUnread} unread)` : 'Open Messages'}
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="max-w-lg">
            <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="profile-form">
              <h3 className="font-bold text-slate-900 mb-5" style={{fontFamily:'Outfit,sans-serif'}}>Profile Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name</label>
                  <input value={user?.name || ''} readOnly
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                  <input value={user?.email || ''} readOnly
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-slate-50 text-slate-500" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">Tenants will use this to contact you</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Role</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="role-selector">
                    {[
                      { value: 'tenant', label: 'Tenant', desc: 'Looking for a place' },
                      { value: 'owner', label: 'Owner', desc: 'I own properties' },
                      { value: 'broker', label: 'Broker', desc: 'I list for others' },
                    ].map(r => (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setSelectedRole(r.value)}
                        data-testid={`role-option-${r.value}`}
                        className={`flex flex-col items-center gap-0.5 px-3 py-3 rounded-xl border-2 text-center transition-all ${
                          selectedRole === r.value
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <span className="text-sm font-semibold">{r.label}</span>
                        <span className="text-[10px] text-slate-400 leading-tight">{r.desc}</span>
                      </button>
                    ))}
                  </div>
                  {selectedRole !== user?.role && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1" data-testid="role-change-notice">
                      <AlertTriangle className="w-3 h-3" /> Role will change on save
                    </p>
                  )}
                </div>
                <button onClick={handleSaveProfile} disabled={savingProfile}
                  className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-60"
                  data-testid="save-profile-button">
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-white rounded-2xl border border-red-200 p-6 mt-5" data-testid="danger-zone">
              <h3 className="font-bold text-red-700 mb-1 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />Danger Zone
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Permanently delete your account and all associated data — listings, saved properties, notifications. This cannot be undone.
              </p>
              <button
                onClick={openDeleteModal}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors"
                data-testid="delete-account-button"
              >
                <Trash2 className="w-4 h-4" />Delete My Account
              </button>
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <DeleteAccountModal
            initialPhone={user?.phone || ''}
            onClose={() => setShowDeleteModal(false)}
            onDeleted={() => { logout(); navigate('/'); }}
          />
        )}

        {/* Share Listing Modal */}
        {shareProperty && (
          <ShareListingModal
            property={shareProperty}
            onClose={() => setShareProperty(null)}
          />
        )}
      </div>
      <Footer />
    </div>
  );
}
